import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Trash2, ArrowRight, ShieldCheck, Truck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartDrawer = () => {
  const { items, removeItem, total, isOpen, setIsOpen } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ artwork_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm"
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[100] w-full max-w-md bg-[rgba(246,240,224,0.95)] backdrop-blur-2xl border-l border-[rgba(42,33,24,0.12)] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-7 py-6 border-b border-[rgba(42,33,24,0.12)]">
              <div>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#C9501C]">
                  Your selection
                </p>
                <h2 className="font-serif-display text-2xl text-[#2A2118] mt-1">Cart</h2>
              </div>
              <button
                data-testid="cart-drawer-close-button"
                onClick={() => setIsOpen(false)}
                className="text-[#7A6C5A] hover:text-[#2A2118] transition-colors"
                aria-label="close cart"
              >
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center" data-testid="cart-empty-state">
                  <p className="font-serif-display italic text-xl text-[#AB9C86]">
                    The gallery wall is bare.
                  </p>
                  <p className="mt-2 text-sm text-[#7A6C5A]">
                    Add an original or a print to begin your collection.
                  </p>
                </div>
              ) : (
                items.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 24 }}
                    className="flex gap-4"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <div className="w-20 h-24 shrink-0 overflow-hidden border border-[rgba(42,33,24,0.12)]">
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-serif-display text-base text-[#2A2118] leading-tight">
                          {item.title}
                        </h3>
                        <button
                          data-testid={`cart-remove-${item.id}`}
                          onClick={() => removeItem(item.id)}
                          className="text-[#AB9C86] hover:text-[#C9501C] transition-colors"
                          aria-label={`remove ${item.title}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <p className="mt-1 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#7A6C5A]">
                        {item.type === "original" ? "Original · 1 of 1" : item.edition || "Archival Print"}
                      </p>
                      <p className="mt-2 font-mono-accent text-sm font-semibold text-[#2A2118]">
                        ${item.price.toLocaleString()}
                      </p>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[rgba(42,33,24,0.12)] px-7 py-6 space-y-4">
                <div className="flex items-center gap-2 text-[11px] text-[#7A6C5A]">
                  <Truck size={14} className="text-[#C9501C]" />
                  <span>Insured global shipping included</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-[#7A6C5A]">
                  <ShieldCheck size={14} className="text-[#C9501C]" />
                  <span>Certificate of authenticity with every piece</span>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-[#7A6C5A]">
                    Subtotal
                  </span>
                  <span data-testid="cart-subtotal" className="font-mono-accent text-xl font-semibold text-[#2A2118]">
                    ${total.toLocaleString()}
                  </span>
                </div>
                <button
                  data-testid="cart-drawer-checkout-button"
                  onClick={checkout}
                  disabled={checkingOut}
                  className="w-full flex items-center justify-center gap-3 bg-[#C9501C] hover:bg-[#f02a4a] disabled:opacity-60 text-[#2A2118] font-mono-accent text-xs uppercase tracking-[0.3em] py-4 transition-colors duration-300"
                >
                  {checkingOut ? "Preparing checkout…" : "Checkout"}
                  {!checkingOut && <ArrowRight size={15} />}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
